import { api } from './client'

export interface CreateSchoolRequest {
  name: string
  shortName?: string
  region?: string
}

export interface InviteRequest {
  targetRole: 'TEACHER' | 'VICE_PRINCIPAL' | 'PRINCIPAL' | 'IT_ADMIN'
}

export interface InviteResponse {
  token: string
  schoolId: string
  targetRole: string
  expiresAt: number
}

export interface JoinResponse {
  schoolId: string
  role: string
}

export interface SchoolTeacher {
  id: string
  email: string
  displayName: string
  role: string
  createdAt: number
  /** 6.1: у сотрудника включена 2FA. */
  totpEnabled?: boolean
}

export interface TeacherStats {
  teacherId: string
  classesCount: number
  studentsCount: number
  gradesLast30d: number
  documentsCount?: number
}

export interface TeacherLlmCost {
  teacherId: string
  displayName: string
  email: string
  requestCount: number
  promptTokens: number
  completionTokens: number
}

export interface SchoolLlmStats {
  days: number
  totalRequests: number
  totalPromptTokens: number
  totalCompletionTokens: number
  perTeacher: TeacherLlmCost[]
}

export interface SchoolDto {
  id: string
  name: string
  shortName?: string | null
  address?: string | null
  region?: string | null
  subscriptionTier: string
  licenseCount: number
  createdAt: number
  /** 6.1: 2FA обязательна для всех сотрудников школы. */
  require2faAll?: boolean
}

export const schoolApi = {
  getSchool: (schoolId: string) =>
    api<SchoolDto>(`/schools/${schoolId}`),

  createSchool: (body: CreateSchoolRequest) =>
    api<SchoolDto>(`/schools`, { method: 'POST', body: JSON.stringify(body) }),

  createInvite: (schoolId: string, targetRole: InviteRequest['targetRole']) =>
    api<InviteResponse>(`/schools/${schoolId}/invite`, {
      method: 'POST',
      body: JSON.stringify({ targetRole }),
    }),

  joinByToken: (token: string) =>
    api<JoinResponse>(`/schools/join`, { method: 'POST', body: JSON.stringify({ token }) }),

  listTeachers: (schoolId: string) =>
    api<SchoolTeacher[]>(`/schools/${schoolId}/teachers`),

  teacherStats: (schoolId: string, targetId: string) =>
    api<TeacherStats>(`/schools/${schoolId}/teacher/${targetId}/stats`),

  changeTeacherRole: (schoolId: string, targetId: string, newRole: string) =>
    api<SchoolTeacher>(`/schools/${schoolId}/teacher/${targetId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ newRole }),
    }),

  removeTeacher: (schoolId: string, targetId: string) =>
    api<{ removed: boolean }>(`/schools/${schoolId}/teacher/${targetId}`, { method: 'DELETE' }),

  /** 6.1: сброс 2FA сотрудника (потерян телефон и резервные коды). */
  resetTwoFactor: (schoolId: string, targetId: string) =>
    api<{ reset: boolean; hadTotp: boolean; sessionsRevoked: number }>(
      `/schools/${schoolId}/teacher/${targetId}/2fa/reset`, { method: 'POST' }),

  /** 6.1: политика школы «2FA обязательна для всех сотрудников». */
  setRequire2faAll: (schoolId: string, require2faAll: boolean) =>
    api<{ schoolId: string; require2faAll: boolean }>(`/schools/${schoolId}/security`, {
      method: 'PUT',
      body: JSON.stringify({ require2faAll }),
    }),

  llmStats: (schoolId: string, days = 30) =>
    api<SchoolLlmStats>(`/schools/${schoolId}/llm-stats?days=${days}`),
}
